const API_URL = import.meta.env.VITE_API_URL || '/api';

interface ApiError {
  message: string;
  status: number;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getAuthToken(): string | null {
    try {
      const stored = localStorage.getItem('ukonnx-auth');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.state?.token || null;
      }
    } catch {
      return null;
    }
    return null;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getAuthToken();
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error: ApiError = {
        message: await response.text(),
        status: response.status,
      };
      throw new Error(error.message || `HTTP ${error.status}`);
    }

    // Handle empty responses
    const text = await response.text();
    if (!text) return {} as T;
    
    return JSON.parse(text);
  }

  // Auth endpoints
  auth = {
    login: async (username: string, password: string) => {
      return this.request<{ token: string }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      });
    },
  };

  // Highscore endpoints
  highscores = {
    getAll: async (params?: { game?: string; limit?: number; offset?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.game) searchParams.set('game', params.game);
      if (params?.limit) searchParams.set('limit', params.limit.toString());
      if (params?.offset) searchParams.set('offset', params.offset.toString());
      
      const query = searchParams.toString();
      return this.request<HighscoreEntry[]>(`/highscores${query ? `?${query}` : ''}`);
    },

    getByGame: async (gameId: string, limit = 10) => {
      return this.request<HighscoreEntry[]>(`/highscores/${gameId}?limit=${limit}`);
    },

    submit: async (entry: HighscoreSubmission) => {
      return this.request<HighscoreEntry>('/highscores', {
        method: 'POST',
        body: JSON.stringify(entry),
      });
    },

    exportCsv: async (gameId?: string) => {
      const query = gameId ? `?game=${gameId}` : '';
      return this.request<{ csv: string }>(`/admin/highscores/export${query}`);
    },

    deleteAll: async (gameId?: string) => {
      const query = gameId ? `?game=${gameId}` : '';
      return this.request<{ deleted: number }>(`/admin/highscores${query}`, {
        method: 'DELETE',
      });
    },
  };

  // Health check
  health = {
    check: async () => {
      return this.request<{ status: string; version: string }>('/health');
    },
  };
}

export const api = new ApiClient(API_URL);

// Types
export interface HighscoreEntry {
  id: string;
  game_id: string;
  player_name: string;
  email?: string;
  score: number;
  level?: number;
  metadata?: Record<string, unknown>;
  created_at: string;
}

export interface HighscoreSubmission {
  game_id: string;
  player_name: string;
  email?: string;
  score: number;
  level?: number;
  metadata?: Record<string, unknown>;
}
